import { useCallback, useEffect, useReducer } from 'react'

import { GridSortDirection } from '@mui/x-data-grid-pro'

import { useGuaranteedScid } from 'common/use-scid'
import { ONE_TIME } from 'routes/outreach/outreach-message-types'

import { Message, MessageResponse, UIFilters } from '../types'
import OutreachMessageContext from './Context'
import { getMessage, updateMessagePriority } from './message-service'
import messageReducer, { initialState } from './messageReducer'

const OutreachMessageProvider = ({ children }: { children: React.ReactNode }) => {
  const scid = useGuaranteedScid()

  const [state, dispatch] = useReducer(messageReducer, initialState)

  /** Sorting Logic */

  const setInitialSortModel = useCallback(() => {
    if (!state.activeView || !state.activeView[state.route] || !state.activeView[state.route]?.sortModel) {
      const defaultSortModel =
        state.route === ONE_TIME
          ? { field: 'ScheduledDate', sort: 'desc' as GridSortDirection }
          : { field: 'CreatedDate', sort: 'desc' as GridSortDirection }
      dispatch({
        type: 'update-column-sort',
        sortModel: defaultSortModel,
      })
    }
  }, [state.activeView, state.route])

  useEffect(() => {
    setInitialSortModel()
  }, [setInitialSortModel])

  /** Filtering Logic */

  const filterMessages = useCallback(
    (filterModel?: UIFilters) => {
      if (!filterModel) {
        // Clear filters
        dispatch({ type: 'set-filtered-messages', messages: undefined })
        return
      }

      let filteredMessages = state.messages || { automated: [], 'one-time': [] }

      /** Status Filter */
      if (filterModel.status && filterModel.status.length > 0) {
        const statusFilterValues = filterModel.status
        if (state.route === ONE_TIME) {
          // Set status filter, if filter is "Draft" check IsDraft field otherwise filter out drafts
          filteredMessages = {
            ...filteredMessages,
            'one-time': filteredMessages?.['one-time'].filter((msg) =>
              statusFilterValues.includes('Draft') && msg.IsDraft
                ? true
                : msg.Status && statusFilterValues.includes(msg.Status) && !msg.IsDraft,
            ) as Message[],
          }
        } else {
          // Set status filter using 'IsDraft' and 'Disabled' fields for automated messages
          filteredMessages = {
            ...filteredMessages,
            automated: filteredMessages?.automated.filter((msg) =>
              statusFilterValues.includes('Draft') && msg.IsDraft && !msg.Disabled
                ? true
                : statusFilterValues.includes('Disabled') && msg.Disabled
                  ? true
                  : !msg.IsDraft && !msg.Disabled && statusFilterValues.includes('Live'),
            ) as Message[],
          }
        }
      }

      /** Audience Filter */
      if (filterModel.audience && filterModel.audience.length > 0) {
        const audienceFilterValues = filterModel.audience
        if (state.route === ONE_TIME) {
          filteredMessages = {
            ...filteredMessages,
            'one-time': filteredMessages['one-time'].filter((msg) => {
              const musts = msg.Segment?.Criteria?.Must || []
              const segmentUserType = musts.find((s) => s.Name === 'UserType')?.Values?.[0]
              const segmentEngagementStage = musts.find((s) => s.Name === 'EngagementStage')
              const hasHighSchoolStudentId = Boolean(
                musts.find((s) => s.Name === 'HighSchoolStudentId' || s.Name === 'UUID'),
              )
              const hasMyMatches = Boolean(musts.find((s) => s.Name === 'MyMatches'))

              // Case 1: Parent audiences
              const matchesParent = audienceFilterValues.includes('Parent') && segmentUserType === 'Parent'

              // Case 2: Followers — include if Student + EngagementStage exists and no HighSchoolStudentId
              const matchesFollowers =
                audienceFilterValues.includes('Followers') &&
                segmentUserType === 'Student' &&
                Boolean(segmentEngagementStage) &&
                !hasHighSchoolStudentId

              // Case 3: StudentIds — include if has HighSchoolStudentId
              const matchesStudentIds = audienceFilterValues.includes('StudentIds') && hasHighSchoolStudentId

              // Case 4: MyMatches — include if has MyMatches
              const matchesMyMatches = audienceFilterValues.includes('MyMatches') && hasMyMatches

              // Return true if ANY of the above matched
              return matchesParent || matchesFollowers || matchesStudentIds || matchesMyMatches
            }),
          }
        } else {
          filteredMessages = {
            ...filteredMessages,
            automated: filteredMessages.automated.filter((msg) => {
              const segmentMusts = msg.Segment?.Criteria?.Must || []
              const segmentUserType = segmentMusts.find((s) => s.Name === 'UserType')?.Values?.[0]
              const allStudentAudiences = ['Followers', 'MyMatches', 'StudentIds']
              const hasAllStudentAudiences = allStudentAudiences.every((audience) =>
                audienceFilterValues.includes(audience),
              )
              const allAudienceFilterValues = [...audienceFilterValues, ...(hasAllStudentAudiences ? ['Student'] : [])]
              return allAudienceFilterValues.includes(segmentUserType || '')
            }),
          }
        }
      }

      /** Trigger Filter */
      if (filterModel.trigger && filterModel.trigger.length > 0) {
        const triggerFilterValues = filterModel.trigger
        if (state.route !== ONE_TIME) {
          filteredMessages = {
            ...filteredMessages,
            automated: filteredMessages.automated.filter(
              (msg) => msg.Trigger && triggerFilterValues.includes(msg.Trigger),
            ) as Message[],
          }
        }
      }
      dispatch({ type: 'set-filtered-messages', messages: filteredMessages })
    },
    [state.messages, state.route],
  )

  useEffect(() => {
    filterMessages(state.activeView?.[state.route]?.filterModel)
  }, [filterMessages, state.activeView, state.activeView?.[state.route], state.route])

  /** Message Actions */

  const fetchMessage = useCallback(async () => {
    await getMessage({ scid })
      .then((response: MessageResponse) => {
        const allMessages = {
          'one-time': response['One-Time'] || [],
          automated: [
            ...(response['On-Follow'] || []),
            ...(response['Marked-Applying'] || []),
            ...(response['Marked-Applied'] || []),
          ],
        }
        const automatedMessagesByTrigger = {
          'On-Follow': response['On-Follow'] || [],
          'Marked-Applying': response['Marked-Applying'] || [],
          'Marked-Applied': response['Marked-Applied'] || [],
        }
        dispatch({ type: 'load-messages', messages: allMessages, automatedMessagesByTrigger })
      })
      .catch(() => {
        dispatch({ type: 'set-error', hasError: true })
      })
  }, [scid])

  useEffect(() => {
    // Fetch Messages on Load
    if (state.isLoading) {
      fetchMessage()
    }
  }, [fetchMessage, state.isLoading])

  /** Priority Reordering */
  const updatePriorityOrder = useCallback(
    async ({ message, priority }: { message: Message; priority: number }) => {
      await updateMessagePriority({
        scid,
        message,
        priority,
      }).catch(() => {
        dispatch({ type: 'set-reorder-error', hasReorderError: true })
      })
      // Refetch messages after updating priorities
      await fetchMessage()
    },
    [scid, fetchMessage],
  )

  return (
    <OutreachMessageContext.Provider
      value={{
        scid,
        state,
        route: state.route,
        fetchMessage,
        messages: state.messages,
        automatedMessagesByTrigger: state.automatedMessagesByTrigger,
        filteredMessages: state.filteredMessages,
        fetchMessageLoading: state.isLoading,
        dispatch,
        activeView: state.activeView,
        updatePriorityOrder,
      }}
    >
      {children}
    </OutreachMessageContext.Provider>
  )
}

export default OutreachMessageProvider
